import React, { useState, useEffect } from 'react';
import './RoomsTable.css';
import { useNavigate,useLocation } from 'react-router-dom';

interface Room {
  codigo_habitacion: string;
  cantidad_ocupantes: number;
  ammenities: string[];
  descripcion: string;
  cantidad_camas: number;
  cantidad_camas_dobles: number;
  opcion1: string;
  opcion2: string | null;
  piso: number;
}

interface Category {
  codigo_categoria: string;
  descripcion_categoria: string;
  habitaciones_disponibles: Room[];
  amenities: string[];
}

interface TarifaBase {
  basePorPersona: number;
  montoPorPersona: number;
}

interface Condicion {
  codigoCondicion: string;
  bases: TarifaBase[];
}

interface Temporada {
  codigoTemporada: string;
  condiciones: Condicion[];
}

interface TarifaCategoria {
  codigoCategoria: string;
  temporadas: Temporada[];
}

interface RoomsData {
  libres?: Category[];
  tarifas_categorias?: TarifaCategoria[];
}

interface Occupant {
  codigo_habitacion: string;
  cantidad_ocupantes: number;
  selected_distribution: 'opcion1' | 'opcion2' | null;
}

const RoomTable: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [occupants, setOccupants] = useState<Occupant[]>([]);
  const [initialCategories, setInitialCategories] = useState<Category[]>([]);
  const [tarifasCategorias, setTarifasCategorias] = useState<TarifaCategoria[]>([]);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [totalOccupants, setTotalOccupants] = useState<number>(0);
  const total_disp = sessionStorage.getItem("total_disp") ? Number(sessionStorage.getItem("total_disp")) : 2;
  const [selectedDistributions, setSelectedDistributions] = useState<{ [roomCode: string]: 'opcion1' | 'opcion2' | null }>({});

  useEffect(() => {
    const roomsData: RoomsData = JSON.parse(sessionStorage.getItem('roomsData') || '{}');
    if (roomsData.libres) {
      setInitialCategories(roomsData.libres);
    }
    if (roomsData.tarifas_categorias) {
      setTarifasCategorias(roomsData.tarifas_categorias);
    }
  }, []);

  

  useEffect(() => {
    // Detectar si el tamaño de la ventana es de dispositivo móvil
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // Considerar móvil cuando el ancho de la ventana es menor o igual a 768px
    };

    // Ejecutar al montar el componente
    handleResize();

    // Agregar el evento de resize para cambiar el estado según el tamaño de la pantalla
    window.addEventListener('resize', handleResize);

    // Limpiar el evento al desmontar el componente
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const router = useNavigate();

  useEffect(() => {
    const handleUnloadOrBack = () => {
      // Elimina la variable deseada del sessionStorage
      sessionStorage.removeItem('dates_adjusted');
    };

    // Escucha el evento beforeunload para detectar recargas o cierres de la página
    window.addEventListener('beforeunload', handleUnloadOrBack);

    // Escucha el evento popstate para detectar el botón de retroceso
    window.addEventListener('popstate', handleUnloadOrBack);

    // Limpieza de los listeners al desmontar el componente
    return () => {
      window.removeEventListener('beforeunload', handleUnloadOrBack);
      window.removeEventListener('popstate', handleUnloadOrBack);
    };
  }, []);


  const handleOccupantsChange = (roomCode: string, event: React.ChangeEvent<HTMLSelectElement>) => {
    const newCount = parseInt(event.target.value);
    const existingOccupantIndex = occupants.findIndex((occupant) => occupant.codigo_habitacion === roomCode);
    let newTotalOccupants = totalOccupants;

    const selectedDistribution = selectedDistributions[roomCode];

    // Crear el nuevo objeto de ocupante con la distribución seleccionada
    const newOccupant = {
      codigo_habitacion: roomCode,
      cantidad_ocupantes: newCount,
      selected_distribution: selectedDistribution,
    };

    const newOccupants = existingOccupantIndex !== -1
      ? [...occupants]
      : [...occupants, newOccupant];

    if (existingOccupantIndex !== -1) {
      newTotalOccupants = newTotalOccupants - occupants[existingOccupantIndex].cantidad_ocupantes + newCount;
      if (newCount === 0) {
        newOccupants.splice(existingOccupantIndex, 1);
      } else {
        newOccupants[existingOccupantIndex] = newOccupant;
      }
    } else {
      newTotalOccupants += newCount;
    }

    if (newTotalOccupants === total_disp) {
      setOccupants(newOccupants);
      setTotalOccupants(newTotalOccupants);
      setWarningMessage('');
    } else if (newTotalOccupants > total_disp) {
      setWarningMessage(`No puede seleccionar más de ${total_disp} huéspedes.`);
    } else {
      setOccupants(newOccupants);
      setTotalOccupants(newTotalOccupants);
      setWarningMessage(`Faltan distribuir huéspedes, por favor distribuya al menos ${total_disp} huéspedes.`);
    }
  };

  const handleSubmit = () => {
    if (totalOccupants === 0) {
      setWarningMessage('Por favor, distribuya a los huéspedes antes de continuar.');
      return;
    }

    if (totalOccupants > total_disp) {
      setWarningMessage(`No puede seleccionar más de ${total_disp} huéspedes.`);
      return;
    }

    if (totalOccupants < total_disp) {
      setWarningMessage(`Faltan distribuir huéspedes, por favor distribuya al menos ${total_disp} huéspedes.`);
      return;
    }

    for (const occupant of occupants) {
      if (!occupant.selected_distribution) {
        setWarningMessage('Por favor seleccione la distribución de camas elegida en todas las habitaciones.');
        return;
      }
    }

    const dataToSend = occupants
      .filter(occupant => occupant.cantidad_ocupantes > 0)
      .map(occupant => {
        const room = initialCategories.flatMap(category => category.habitaciones_disponibles)
          .find(room => room.codigo_habitacion === occupant.codigo_habitacion);

        const tipo_habitacion = initialCategories.find(category =>
          category.habitaciones_disponibles.some(room => room.codigo_habitacion === occupant.codigo_habitacion)
        )?.descripcion_categoria || '';

        const informacion = occupant.selected_distribution === 'opcion1'
          ? room?.opcion1
          : room?.opcion2;

        return {
          ...occupant,
          tipo_habitacion: tipo_habitacion,
          informacion: informacion || ''
        };
      });

    sessionStorage.setItem('occupants', JSON.stringify(dataToSend));
    const informacion_adicional_habitaciones = dataToSend.map(habitacion => ({
      habitacion: habitacion.codigo_habitacion,
      informacion: habitacion.informacion
    }));

    const resultado = { informacion_adicional_habitaciones };

    // Guarda el objeto en sessionStorage
    sessionStorage.setItem('informacion_adicional_reserva', JSON.stringify(resultado));

    if (warningMessage === '') {
      router('/guest-form');
    }
  };

  const handleCheckboxChange = (roomCode: string, distribution: 'opcion1' | 'opcion2') => {
    setWarningMessage('');
    setSelectedDistributions(prevDistributions => {
      if (prevDistributions[roomCode] === distribution) {
        return {
          ...prevDistributions,
          [roomCode]: null
        };
      }

      return {
        ...prevDistributions,
        [roomCode]: distribution
      };
    });

    setOccupants(prevOccupants => {
      const updatedOccupants = prevOccupants.map(occupant =>
        occupant.codigo_habitacion === roomCode
          ? { ...occupant, selected_distribution: distribution }
          : occupant
      );

      return updatedOccupants;
    });
  };

  const parseOpcion = (opcion: string | null | undefined) => {
    if (!opcion) {
      return { simpleBeds: 0, doubleBeds: 0 };
    }
  
    const sanitizedOption = opcion.replace(/\s+/g, '').toUpperCase();
    const simpleBeds = (sanitizedOption.match(/I/g) || []).length;
    const doubleBeds = (sanitizedOption.match(/X/g) || []).length;
  
    return { simpleBeds, doubleBeds };
  };

  const camaSimpleSVG = (
    <svg width="20" height="30" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 80">
      <title>Bed-hotel-single bed-sleep</title>
      <g>
        <title>Layer 1</title>
        <g id="svg_1">
          <path d="m49,9l-34,0a2,2 0 0 0 -2,2l0,56a2,2 0 0 0 2,2l34,0a2,2 0 0 0 2,-2l0,-56a2,2 0 0 0 -2,-2zm-34,19l34,0l0,4l-34,0l0,-4zm34,-17l0,15l-34,0l0,-15l34,0zm-34,56l0,-33l34,0l0,33l-34,0z" id="svg_2" />
          <path d="m26,24l12,0a3,3 0 0 0 3,-3l0,-5a3,3 0 0 0 -3,-3l-12,0a3,3 0 0 0 -3,3l0,5a3,3 0 0 0 3,3zm-1,-8a1,1 0 0 1 1,-1l12,0a1,1 0 0 1 1,1l0,5a1,1 0 0 1 -1,1l-12,0a1,1 0 0 1 -1,-1l0,-5z" id="svg_3" />
        </g>
      </g>
    </svg>
  );

  const camaDobleSVG = (
    <svg width="18" height="30" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 80">
      <title>Bed-bedroom-furniture-hotel-sleep-twin</title>
      <g>
        <title>Layer 1</title>
        <g id="svg_1">
          <path d="m57,11l-50,0a2,2 0 0 0 -2,2l0,56a2,2 0 0 0 2,2l50,0a2,2 0 0 0 2,-2l0,-56a2,2 0 0 0 -2,-2zm-50,19l50,0l0,4l-50,0l0,-4zm50,-17l0,15l-50,0l0,-15l50,0zm-50,56l0,-33l50,0l0,33l-50,0z" id="svg_2" />
          <path d="m14,26l12,0a3,3 0 0 0 3,-3l0,-5a3,3 0 0 0 -3,-3l-12,0a3,3 0 0 0 -3,3l0,5a3,3 0 0 0 3,3zm-1,-8a1,1 0 0 1 1,-1l12,0a1,1 0 0 1 1,1l0,5a1,1 0 0 1 -1,1l-12,0a1,1 0 0 1 -1,-1l0,-5z" id="svg_3" />
          <path d="m38,26l12,0a3,3 0 0 0 3,-3l0,-5a3,3 0 0 0 -3,-3l-12,0a3,3 0 0 0 -3,3l0,5a3,3 0 0 0 3,3zm-1,-8a1,1 0 0 1 1,-1l12,0a1,1 0 0 1 1,1l0,5a1,1 0 0 1 -1,1l-12,0a1,1 0 0 1 -1,-1l0,-5z" id="svg_4" />
        </g>
      </g>
    </svg>
  );

  const amenities_icons = [
    { name: "mampara chica", icon: "la-shower" },
    { name: "bidematic", icon: "la-toilet" },
    { name: "colchon", icon: "la-bed" },
    { name: "cuna", icon: "la-baby-carriage" },
    { name: "desayuno", icon: "la-coffee" },
    { name: "tina", icon: "la-bath" },
    { name: "aire acondicionado", icon: "la-snowflake" },
    { name: "spa", icon: "la-spa" },
    { name: "bidet", icon: "la-toilet" },
    { name: "piscina exterior", icon: "la-swimmer" },
    { name: "personas", icon: "la-user" },
    { name: 'cama_simple', icon: 'la-bed' }, // Icono de cama simple
    { name: 'cama_doble', icon: 'la-bed' } // Icono de cama doble
  ];
  const getPrecioPorPersona = (codigoCategoria: string, cantidadOcupantes: number): number => {
    const categoria = tarifasCategorias.find(tarifa => tarifa.codigoCategoria === codigoCategoria);
    if (!categoria) return 0;

    const temporada = categoria.temporadas[0]; // Asumiendo que siempre hay al menos una temporada
    const condicion = temporada.condiciones[0]; // Asumiendo que siempre hay al menos una condición
    const base = condicion.bases.find(base => base.basePorPersona === cantidadOcupantes);

    return base ? base.montoPorPersona : 0;

  };

  // Versión mobile
  if (isMobile) {
    return (
      <div className='listingSection__wrap'>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">
          Distribucion de los huespedes
        </h2>
        <p>Por favor, seleccione la cantidad de huespedes por habitación</p>
        <div className='w-full max-h-[550px] overflow-y-auto'>
          <table className='w-full'>
            <tbody className='text-center text-xs'>
              {initialCategories.map((category, categoryIndex) => (
                <React.Fragment key={categoryIndex}>
                  <tr>
                    <td colSpan={7} className='font-bold text-blue-600 py-3 text-left md:text-center'>
                      Habitaciones disponibles categoría {category.descripcion_categoria}:
                    </td>
                  </tr>
                  {category.habitaciones_disponibles.map((room, roomIndex) => {
                    const opcion1Distribution = parseOpcion(room.opcion1);
                    const opcion2Distribution = room.opcion2 ? parseOpcion(room.opcion2) : null;

                    const amenitiesWithIcons = room.ammenities.filter(amenity => amenities_icons.some(icon => icon.name === amenity));
                    const amenitiesWithoutIcons = room.ammenities.filter(amenity => !amenities_icons.some(icon => icon.name === amenity));

                    return (
                      <tr key={`${categoryIndex}-${roomIndex}`} className='border border-gray-200 rounded-lg flex flex-col md:table-row'>
                        <td className='pl-4 pr-3 pb-3 pt-3 md:border-none'>
                          <div className="font-semibold text-blue-600">Habitación N° {room.codigo_habitacion.split('-')[1]}</div>
                          <div className="font-semibold text-blue-600">Piso: {room.piso}</div>
                          {opcion1Distribution.simpleBeds > 0 && (
                            <div className='pt-1 flex justify-start items-center'>
                              <input
                                type="checkbox"
                                checked={selectedDistributions[room.codigo_habitacion] === 'opcion1'}
                                onChange={() => handleCheckboxChange(room.codigo_habitacion, 'opcion1')}
                                className='mr-2'
                              />
                              <span className="pl-0">Opción 1:</span>
                              <span className="flex justify-start items-center text-blue-600">
                                {Array.from({ length: opcion1Distribution.simpleBeds }).map((_, index) => (
                                  <span key={index} className="px-1 text-lg text-black">{camaSimpleSVG}</span>
                                ))}
                                {Array.from({ length: opcion1Distribution.doubleBeds }).map((_, index) => (
                                  <span key={index} className="px-1 text-lg text-black">{camaDobleSVG}</span>
                                ))}
                              </span>
                            </div>
                          )}
                          {opcion2Distribution && (
                            <div className='pt-1 flex justify-start items-center'>
                              <input
                                type="checkbox"
                                checked={selectedDistributions[room.codigo_habitacion] === 'opcion2'}
                                onChange={() => handleCheckboxChange(room.codigo_habitacion, 'opcion2')}
                                className='mr-2'
                              />
                              <span className="pl-0">Opción 2:</span>
                              <span className="flex justify-start items-center text-blue-600">
                                {Array.from({ length: opcion2Distribution.simpleBeds }).map((_, index) => (
                                  <span key={index} className="px-1 text-lg text-black">{camaSimpleSVG}</span>
                                ))}
                                {Array.from({ length: opcion2Distribution.doubleBeds }).map((_, index) => (
                                  <span key={index} className="px-1 text-lg text-black">{camaDobleSVG}</span>
                                ))}
                              </span>
                            </div>
                          )}
                          <div className='pt-1 flex justify-start items-center'>
                            {amenitiesWithIcons.map((amenity, amenityIndex) => {
                              const amenityIcon = amenities_icons.find(icon => icon.name === amenity);
                              return (
                                <span key={amenityIndex} className="flex justify-start items-center">
                                  <i className={`la ${amenityIcon?.icon} px-1 text-lg`} aria-hidden="true"></i>
                                  <span className="">{amenity}</span>
                                </span>
                              );
                            })}
                          </div>
                          <div className='pt-1 flex justify-start items-center'>
                            {amenitiesWithoutIcons.map((amenity, amenityIndex) => (
                              <span key={amenityIndex} className="inline-block mr-1">
                                <i className={'la la-check text-green-600 px-1 text-lg '} aria-hidden="true"></i>{amenity}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className='z-1 text-center flex justify-between mx-11'>
                          <div>
                            <div className="font-semibold text-blue-600">Capacidad:</div>
                            <div className='flex justify-center'>
                              {Array.from({ length: room.cantidad_ocupantes }).map((_, index) => (
                                <span key={index}>
                                  <i className="la la-user text-lg" aria-hidden="true"></i>
                                </span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div className="font-semibold text-blue-600">Precio por persona:</div>
                            <div className='text-lg'>
                              ${getPrecioPorPersona(category.codigo_categoria, room.cantidad_ocupantes)}
                            </div>
                          </div>
                        </td>
                        <td className='pb-3 pr-3 text-left flex justify-center'>
                          <div className=''>
                            <div className="font-semibold text-lg text-blue-600">Huespedes:</div>
                            <div className='flex justify-center mt-3'>
                              <select className=' h-[60px] w-[100px] text-xl' onChange={(event) => handleOccupantsChange(room.codigo_habitacion, event)}>
                                {Array.from({ length: room.cantidad_ocupantes + 1 }).map((_, index) => (
                                  <option key={index} value={index}>{index}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </td>
                        
                        <div className='flex justify-center mt-3 mb-3'>
                        <button className='w-1/3 bg-blue-600 text-white text-sm h-12 rounded-full' onClick={handleSubmit}>Reservar</button>
                        </div>
                        {warningMessage && (
                          <div className='text-center text-red-600 mb-3'>
                            {warningMessage}
                          </div>
                        )}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <p>* El total por persona corresponde al precio de la habitación por noche</p>
        </div>
        {warningMessage && (
          <div className='text-center text-red-600'>
            {warningMessage}
          </div>
        )}
        <div className='flex justify-center'>
          <button
            className='nc-Button h-auto items-center justify-center rounded-full transition-colors text-sm w-fit sm:align-middle sm:text-base font-medium px-4 py-3 sm:px-6 ttnc-ButtonPrimary disabled:bg-opacity-70 bg-primary-6000 hover:bg-primary-700 text-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-6000 dark:focus:ring-offset-0'
            onClick={handleSubmit}
          >
            Continuar
          </button>
        </div>
      </div>
    );
  }

  // Versión desktop
  return (
    <div className='listingSection__wrap'>
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">
        Distribucion de los huespedes
      </h2>
      <p>Por favor, seleccione la cantidad de huespedes por habitación</p>
      <div className='w-full max-h-[550px] overflow-y-auto'>
        <table className='w-full'>
          <thead className='text-center items-center pb-5 text-xs overflow-x-auto sticky top-0 bg-blue-50 z-10 hidden md:table-header-group'>
            <tr className='w-full border-b items-center pt-2'>
              <th className='pb-3 pl-4 pt-3'>Habitación</th>
              <th className='pb-3 pr-3 pl-0 pt-3'>Piso</th>
              <th className='pb-3 pr-3 pl-0 pt-3'>Capacidad</th>
              <th className='pb-3 pt-3'>Precio por persona</th>
              <th className='pb-3 pt-3 text-left'>Huespedes</th>
            </tr>
          </thead>
          <tbody className='text-center text-xs'>
            {initialCategories.map((category, categoryIndex) => (
              <React.Fragment key={categoryIndex}>
                <tr>
                  <td colSpan={7} className='font-bold text-blue-600 py-3 text-left md:text-center'>
                    Habitaciones disponibles categoría {category.descripcion_categoria}:
                  </td>
                </tr>
                {category.habitaciones_disponibles.map((room, roomIndex) => {
                  const opcion1Distribution = parseOpcion(room.opcion1);
                  const opcion2Distribution = room.opcion2 ? parseOpcion(room.opcion2) : null;

                  const amenitiesWithIcons = room.ammenities.filter(amenity => amenities_icons.some(icon => icon.name === amenity));
                  const amenitiesWithoutIcons = room.ammenities.filter(amenity => !amenities_icons.some(icon => icon.name === amenity));

                  return (
                    <tr key={`${categoryIndex}-${roomIndex}`} className='border border-gray-200 rounded-lg flex flex-col md:table-row'>
                      <td className='pl-4 pr-3 pb-3 pt-3 md:border-none flex flex-col'>
                        <div className="font-semibold self-start text-blue-600"> Habitación N° {room.codigo_habitacion.split('-')[1]}</div>
                        {opcion1Distribution.simpleBeds > 0 && (
                          <div className='pt-1 flex justify-start items-center'>
                            <input
                              type="checkbox"
                              checked={selectedDistributions[room.codigo_habitacion] === 'opcion1'}
                              onChange={() => handleCheckboxChange(room.codigo_habitacion, 'opcion1')}
                              className='mr-2'
                            />
                            <span className="pl-0">Opción 1:</span>
                            <span className="flex justify-start items-center text-blue-600">
                              {Array.from({ length: opcion1Distribution.simpleBeds }).map((_, index) => (
                                <span key={index} className="px-1 text-lg text-black">{camaSimpleSVG}</span>
                              ))}
                              {Array.from({ length: opcion1Distribution.doubleBeds }).map((_, index) => (
                                <span key={index} className="px-1 text-lg text-black">{camaDobleSVG}</span>
                              ))}
                            </span>
                          </div>
                        )}
                        {opcion2Distribution && (
                          <div className='pt-1 flex justify-start items-center'>
                            <input
                              type="checkbox"
                              checked={selectedDistributions[room.codigo_habitacion] === 'opcion2'}
                              onChange={() => handleCheckboxChange(room.codigo_habitacion, 'opcion2')}
                              className='mr-2'
                            />
                            <span className="pl-0">Opción 2:</span>
                            <span className="flex justify-start items-center text-blue-600">
                              {Array.from({ length: opcion2Distribution.simpleBeds }).map((_, index) => (
                                <span key={index} className="px-1 text-lg text-black">{camaSimpleSVG}</span>
                              ))}
                              {Array.from({ length: opcion2Distribution.doubleBeds }).map((_, index) => (
                                <span key={index} className="px-1 text-lg text-black">{camaDobleSVG}</span>
                              ))}
                            </span>
                          </div>
                        )}
                        <div className='pt-1 flex justify-start items-center'>
                          {amenitiesWithIcons.map((amenity, amenityIndex) => {
                            const amenityIcon = amenities_icons.find(icon => icon.name === amenity);
                            return (
                              <span key={amenityIndex} className="flex justify-start items-center">
                                <i className={`la ${amenityIcon?.icon} px-1 text-lg`} aria-hidden="true"></i>
                                <span className="">{amenity}</span>
                              </span>
                            );
                          })}
                        </div>
                        <div className='pt-1 flex justify-start items-center'>
                          {amenitiesWithoutIcons.map((amenity, amenityIndex) => (
                            <span key={amenityIndex} className="inline-block mr-1">
                              <i className={'la la-check text-green-600 px-1 text-lg '} aria-hidden="true"></i>{amenity}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className='z-1 text-center'>
                        <div className='flex justify-center'>
                            <span >
                              {room.piso}
                            </span>
                        </div>
                      </td>
                      <td className='z-1 text-center'>
                        <div className='flex justify-center'>
                          {Array.from({ length: room.cantidad_ocupantes }).map((_, index) => (
                            <span key={index}>
                              <i className="la la-user text-lg" aria-hidden="true"></i>
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className='text-center pr-4'>
                        <div className='text-lg'>
                          ${getPrecioPorPersona(category.codigo_categoria, room.cantidad_ocupantes)}
                        </div>
                      </td>
                      <td className='pb-3 text-center'>
                        <select className='w-auto' onChange={(event) => handleOccupantsChange(room.codigo_habitacion, event)}>
                          {Array.from({ length: room.cantidad_ocupantes + 1 }).map((_, index) => (
                            <option key={index} value={index}>{index}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <p>* El total por persona corresponde al precio de la habitación por noche</p>
      </div>
      {warningMessage && (
        <div className='text-center text-red-600'>
          {warningMessage}
        </div>
      )}
      <div className='flex justify-center'>
        <button
          className='nc-Button h-auto items-center justify-center rounded-full transition-colors text-sm w-fit sm:align-middle sm:text-base font-medium px-4 py-3 sm:px-6 ttnc-ButtonPrimary disabled:bg-opacity-70 bg-primary-6000 hover:bg-primary-700 text-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-6000 dark:focus:ring-offset-0'
          onClick={handleSubmit}
        >
          Continuar
        </button>
      </div>
    </div>
  );
};

export default RoomTable;